import {
  DataGridBox,
  ZebraDataGrid,
  ConfirmDialog,
  CustomToolbar,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import {
  Box,
  Chip,
  alpha,
  Select,
  Button,
  Tooltip,
  MenuItem,
  useTheme,
  Typography,
  InputLabel,
  FormControl,
  Grid2 as Grid,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PersonnelHolidayItem } from 'library/types';
import { FormattedMessage, useIntl } from 'react-intl';
import DownloadIcon from '@mui/icons-material/Download';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { renderDataGridLocaleText, renderFormattedDate } from 'utils';
import { CreateHolidayDialog, DocumentPreviewDialog } from './components';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import { selectPermissionCodes } from 'store/features/permissions/selector';

const HOLIDAY_STATUS = {
  APPROVED: 'O',
  PENDING: 'B',
  CANCELLED: 'I',
  REJECTED: 'R',
} as const;

const PersonnelHoliday = () => {
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState<PersonnelHolidayItem[]>([]);
  const [year, setYear] = useState<number>(() => dayjs().year());
  const [totalHolidayCount, setTotalHolidayCount] = useState<number>(0);

  // Document preview state
  const [previewData, setPreviewData] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState<{ processNo: string; entryDate: string } | null>(null);

  // Generate Year range (last 7 years ending with the current year)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const result = [];

    for (let i = 6; i >= 0; i--) {
      result.push(currentYear - i);
    }

    return result;
  }, []);

  const holidayBalanceColor = useMemo(() => {
    if (totalHolidayCount < 0) return theme.palette.error.main;
    if (totalHolidayCount === 0) return theme.palette.warning.main;
    return theme.palette.success.main;
  }, [totalHolidayCount, theme]);

  // Columns definition
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'order',
        headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
        align: 'center',
        headerAlign: 'center',
        flex: 0.3,
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
        editable: false,
      },
      {
        field: 'entryDate',
        headerName: intl.formatMessage({ id: 'personnelHoliday.entryDate', defaultMessage: 'Talep Tarihi' }),
        flex: 0.8,
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
      },
      {
        field: 'startTime',
        headerName: intl.formatMessage({ id: 'activityManaging.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        align: 'center',
        headerAlign: 'center',
        flex: 1.2,
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
      {
        field: 'endTime',
        headerName: intl.formatMessage({ id: 'activityManaging.endDate', defaultMessage: 'Bitiş Tarihi' }),
        align: 'center',
        headerAlign: 'center',
        flex: 1.2,
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
      {
        field: 'day',
        headerName: intl.formatMessage({ id: 'personnelHoliday.holidayDay', defaultMessage: 'İzin Günü' }),
        align: 'center',
        headerAlign: 'center',
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'users.status', defaultMessage: 'Durum' }),
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
        renderCell: (params) => {
          const status = params.value as string;
          let label = '';
          let colorKey: 'success' | 'primary' | 'warning' | 'error' | 'info' = 'info';

          if (status === HOLIDAY_STATUS.APPROVED) {
            label = intl.formatMessage({ id: 'personnelHoliday.status.approved', defaultMessage: 'Onaylandı' });
            colorKey = 'success';
          } else if (status === HOLIDAY_STATUS.PENDING) {
            label = intl.formatMessage({ id: 'personnelHoliday.status.pending', defaultMessage: 'Bekliyor' });
            colorKey = 'primary';
          } else if (status === HOLIDAY_STATUS.CANCELLED) {
            label = intl.formatMessage({ id: 'personnelHoliday.status.cancelled', defaultMessage: 'İptal Edildi' });
            colorKey = 'warning';
          } else if (status === HOLIDAY_STATUS.REJECTED) {
            label = intl.formatMessage({ id: 'personnelHoliday.status.rejected', defaultMessage: 'Reddedildi' });
            colorKey = 'error';
          }

          return (
            <Chip
              size="small"
              label={label}
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: theme.palette[colorKey].main,
                border: `1px solid ${alpha(theme.palette[colorKey].main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                cursor: 'default',
              }}
            />
          );
        },
      },
      {
        field: 'approver',
        headerName: intl.formatMessage({ id: 'personnelHoliday.approver', defaultMessage: 'Onaylayan' }),
        flex: 1,
      },
      {
        field: 'documentName',
        headerName: intl.formatMessage({ id: 'personnelHoliday.document', defaultMessage: 'Belge' }),
        flex: 1.2,
        renderCell: (params) => {
          const row = params.row as PersonnelHolidayItem;
          if (!row.document) return '-';

          return (
            <Chip
              icon={<FilePresentIcon sx={{ fontSize: '16px !important' }} />}
              label={row.documentName}
              onClick={() => handlePreview(row.document, row.documentName)}
              size="small"
              variant="outlined"
              sx={{
                maxWidth: '100%',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                borderColor: alpha(theme.palette.text.secondary, 0.2),
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main,
                  },
                },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
        flex: 0.5,
        getActions: (params) => {
          const row = params.row as PersonnelHolidayItem;
          const isApproved = row.status === HOLIDAY_STATUS.APPROVED;
          const canDelete = permissionCodes.includes('delete-holiday');
          const isPastEndDate = dayjs().startOf('day').diff(dayjs(row.endTime).startOf('day'), 'day') > 0;

          const tooltipTitle = !canDelete
            ? intl.formatMessage({
                id: 'personnelHoliday.noDeletePermission',
                defaultMessage: 'Silme Yetkiniz Yoktur',
              })
            : isApproved
              ? intl.formatMessage({
                  id: 'personnelHoliday.cannotDeleteApproved',
                  defaultMessage: 'Onaylanmış izinler silinemez!',
                })
              : isPastEndDate
                ? intl.formatMessage({
                    id: 'personnelHoliday.cannotDeletePastEndDate',
                    defaultMessage: 'Bitiş tarihi geçmiş izinler silinemez',
                  })
                : intl.formatMessage({ id: 'dashboard.delete', defaultMessage: 'Sil' });

          const isDisabled = isApproved || !canDelete || isPastEndDate;

          return [
            <GridActionsCellItem
              key="delete"
              icon={
                <Tooltip title={tooltipTitle} arrow>
                  <DeleteIcon fontSize="small" style={{ pointerEvents: 'auto' }} />
                </Tooltip>
              }
              label={tooltipTitle}
              disabled={isDisabled}
              onClick={() => handleDeleteClick(row.processNo, row.entryDate)}
              color="error"
            />,
          ];
        },
      },
    ],
    [intl, theme, permissionCodes]
  );

  // Fetch leave list
  const getValues = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getPersonnelHolidays(year);

      if (response.isSuccess && response.data) {
        setData(response.data.items || []);
        setTotalHolidayCount(response.data.totalHolidayCount || 0);
      }
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.loadError',
          defaultMessage: 'Veriler yüklenirken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [year, intl, enqueueSnackbar]);

  // Download example document
  const handleDownloadExample = async () => {
    try {
      setDownloading(true);

      const response = await apiService.getPersonnelHolidayExample();

      if (response) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response);
        link.download = 'ornek_izin_belgesi.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        enqueueSnackbar(
          intl.formatMessage({
            id: 'personnelHoliday.exampleDownloadSuccess',
            defaultMessage: 'Örnek izin dosyası başarıyla indirildi.',
          }),
          { variant: 'success' }
        );
      }
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.exampleDownloadError',
          defaultMessage: 'Örnek dosya indirilirken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
    } finally {
      setDownloading(false);
    }
  };

  // Preview document
  const handlePreview = (documentBase64: string, documentName: string) => {
    setPreviewData(documentBase64);
    setPreviewName(documentName);
    setPreviewOpen(true);
  };

  // Handle Delete Click
  const handleDeleteClick = (processNo: string, entryDate: string) => {
    setSelectedToDelete({ processNo, entryDate });
    setDeleteOpen(true);
  };

  // Confirm delete leave request
  const handleConfirmDelete = async () => {
    if (!selectedToDelete) return;

    try {
      setDeleting(true);

      const response = await apiService.deletePersonnelHoliday(selectedToDelete);

      if (response.isSuccess) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'personnelHoliday.deleteSuccess',
            defaultMessage: 'İzin talebi başarıyla silindi.',
          }),
          { variant: 'success' }
        );
        setDeleteOpen(false);
        setSelectedToDelete(null);
        await getValues();
      }
    } catch {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.deleteError',
          defaultMessage: 'İzin talebi silinirken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    getValues();
  }, [year, getValues]);

  return (
    <>
      <Grid container spacing={3} sx={{ width: '100%', mb: 3 }}>
        {/* Filters, Kalan İzin and Create Action Row */}
        <Grid
          size={{ xs: 12 }}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="year-filter-label">
                <FormattedMessage id="personnelHoliday.year" defaultMessage="Yıl" />
              </InputLabel>
              <Select
                labelId="year-filter-label"
                value={year}
                label={intl.formatMessage({ id: 'personnelHoliday.year', defaultMessage: 'Yıl' })}
                onChange={(e) => setYear(Number(e.target.value))}>
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExample}
              disabled={downloading}
              size="medium">
              <FormattedMessage id="personnelHoliday.downloadExample" defaultMessage="Örnek İzin Dosyası İndir" />
            </Button>
          </Box>

          <Chip
            icon={<BeachAccessIcon />}
            variant="outlined"
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <FormattedMessage
                  id="personnelHoliday.remainingDays"
                  defaultMessage="Kalan İzin Günü: {count} Gün"
                  values={{
                    count: intl.formatNumber(totalHolidayCount, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }),
                  }}
                />
              </Typography>
            }
            sx={{
              px: 1.5,
              py: 2,
              borderRadius: 2,
              borderColor: alpha(holidayBalanceColor, 0.3),
              backgroundColor: alpha(holidayBalanceColor, 0.05),
              color: holidayBalanceColor,
              transition: 'all 0.3s ease',
              '& .MuiChip-icon': {
                color: holidayBalanceColor,
              },
            }}
          />

          <PermissionHandler requiredPermissionCodes={['create-holiday']}>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreate(true)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.8),
                },
              }}>
              <FormattedMessage id="personnelHoliday.createTitle" defaultMessage="İzin Talebi Oluştur" />
            </Button>
          </PermissionHandler>
        </Grid>
      </Grid>

      {/* Grid Table */}
      <DataGridBox>
        <ZebraDataGrid
          pagination
          columns={columns}
          rows={data}
          getRowId={(row) => `${row.processNo}_${row.entryDate}`}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableRowSelectionOnClick
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>

      {/* Create Dialog */}
      <CreateHolidayDialog open={openCreate} handleClose={() => setOpenCreate(false)} refresh={getValues} />

      {/* Preview Dialog */}
      {previewData && (
        <DocumentPreviewDialog
          open={previewOpen}
          handleClose={() => {
            setPreviewOpen(false);
            setPreviewData('');
            setPreviewName('');
          }}
          documentData={previewData}
          documentName={previewName}
        />
      )}

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        confirmColor="error"
        confirmLabel={<FormattedMessage id="dashboard.delete" defaultMessage="Sil" />}
        title={<FormattedMessage id="personnelHoliday.deleteTitle" defaultMessage="İzin Talebi Sil" />}
        description={
          <FormattedMessage
            id="personnelHoliday.deleteConfirm"
            defaultMessage="Bu izin talebini silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
          />
        }
      />
    </>
  );
};

export default PersonnelHoliday;
